import { useState } from "react";
import { Link } from "react-router-dom";

import {
  type OptionItem,
  OptionType,
  Status,
  useGetOptionsQuery,
} from "@/api/admin";
import { type IGropType, useGetGroupsQuery } from "@/api/admin/groups";
import { AdminSections } from "@/api/admin/teamRoles";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type StatusVariants,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { usePermission } from "@/services/casl/usePermission";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useParamsFromUrl } from "@/utils/hooks/useParamsFromUrl";
import { useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig, isOptionItem } from "../type";

enum AccountGroupsColumn {
  GROUP_NAME = "Group name",
  REFERENCE_NUMBER = "Reference number",
  DATE_TIME = "Created at",
  LEGAL_ENTITY = "Legal entity",
  COUNTRY = "Country",
  ACTIVITY = "Status",
  TOTAL_BRANDS = "Total brands under group",
}

const TABLE_HEADERS: TableHeaderType = Object.values(AccountGroupsColumn);

const AccountGroupsTable = () => {
  const hasPermissionGroup = usePermission(AdminSections.GROUP);
  const hasPermissionBrandList = usePermission(AdminSections.BRANDS_LIST);
  const [searchValue, setSearchValue] = useState("");

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Countries,
    OptionType.LegalEntities,
  ]);

  const countryNames = optionsData?.countries?.map(c => c.name) ?? [];

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: { country: "" },
      },
      searchValue
    );

  useParamsFromUrl(setTableParams, ["legalEntityId"]);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const selectedCountry = selectedMap[AccountGroupsColumn.COUNTRY]?.[0] ?? "";
    const selectedActivity =
      selectedMap[AccountGroupsColumn.ACTIVITY]?.[0] ?? "";
    const selectedBrands = selectedMap["Brands"]?.[0] ?? "";
    const selectedLegalEntity = selectedMap["Legal entity"]?.[0] ?? "";

    setTableParams(prev => ({
      ...prev,
      country:
        typeof selectedCountry === "string" ? selectedCountry : undefined,
      legalEntityId: isOptionItem(selectedLegalEntity)
        ? selectedLegalEntity.id
        : undefined,
      active: selectedActivity
        ? selectedActivity === Status.INACTIVE
          ? false
          : true
        : undefined,
      hasBrands: selectedBrands
        ? selectedBrands === "With brands"
          ? true
          : false
        : undefined,
      offset: 0,
    }));
  };

  const { data: groups, isPending } = useGetGroupsQuery(tableParams);

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [AccountGroupsColumn.COUNTRY]: countryNames,
    [AccountGroupsColumn.LEGAL_ENTITY]: optionsData?.legalEntities ?? [],
    ["Brands"]: ["With brands", "No brands"],
    [AccountGroupsColumn.ACTIVITY]: [Status.ACTIVE, Status.INACTIVE],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [AccountGroupsColumn.COUNTRY]: { type: "role", isSingleSelect: true },
    [AccountGroupsColumn.LEGAL_ENTITY]: {
      type: "search",
      placeholder: "Search legal entity",
      isSingleSelect: true,
    },
    ["Brands"]: { type: "role", isSingleSelect: true },
    [AccountGroupsColumn.ACTIVITY]: { type: "status", isSingleSelect: true },
  };

  const STATUS_VARIANT_MAP: StatusVariants = {
    Active: { badge: "success", dot: "#65A30D" },
  };

  const columns: ColumnConfig<IGropType>[] = [
    {
      key: "name",
      label: AccountGroupsColumn.GROUP_NAME,
      visible: flags[AccountGroupsColumn.GROUP_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        return hasPermissionGroup ? (
          <TableCellLink last={isLast}>
            <Link
              to={PATHS.ADMIN.ACCOUNT_GROUP_DETAILS(row?.id || "")}
              data-testid={`account-groups-cell-id-${row.id}`}
            >
              {row.name}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{row.name}</TableCellText>
        );
      },
    },
    {
      key: "referenceNumber",
      label: AccountGroupsColumn.REFERENCE_NUMBER,
      visible: flags[AccountGroupsColumn.REFERENCE_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.referenceNumber?.toString() || ""}
        </TableCellText>
      ),
    },
    {
      key: "createdAt",
      label: AccountGroupsColumn.DATE_TIME,
      visible: flags[AccountGroupsColumn.DATE_TIME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "legalEntityName",
      label: AccountGroupsColumn.LEGAL_ENTITY,
      visible: flags[AccountGroupsColumn.LEGAL_ENTITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.legalEntityName || "-"}
        </TableCellText>
      ),
    },
    {
      key: "country",
      label: AccountGroupsColumn.COUNTRY,
      visible: flags[AccountGroupsColumn.COUNTRY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.country}</TableCellText>
      ),
    },
    {
      key: "active",
      label: AccountGroupsColumn.ACTIVITY,
      visible: flags[AccountGroupsColumn.ACTIVITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge
          last={isLast}
          stage={row.active ? "success" : "default"}
        >
          {row.active ? Status.ACTIVE : Status.INACTIVE}
        </TableCellBadge>
      ),
    },
    {
      key: "totalBrands",
      label: AccountGroupsColumn.TOTAL_BRANDS,
      visible: flags[AccountGroupsColumn.TOTAL_BRANDS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),

      cellRenderer: (row, isLast) => {
        return hasPermissionBrandList && row?.totalBrands ? (
          <TableCellLink
            last={isLast}
            classes="w-[10%]"
            data-testid={`account-groups-cell-total-${row.totalBrands}`}
          >
            <Link
              to={`${PATHS.ADMIN.ACCOUNT}?offset=0&limit=25&groupId=${row.id}#brands`}
              data-testid={`account-groups-cell-total-link-${row.totalBrands}`}
            >
              {row.totalBrands}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{row.totalBrands || "-"}</TableCellText>
        );
      },
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_VARIANT_MAP}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isPending || isOptionPending ? (
        <Loader />
      ) : (
        <DynamicTable<IGropType>
          data={groups?.items || []}
          columns={columns}
          next={handleNext}
          prev={handlePrev}
          searchValue={searchValue}
          tableParams={groups}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AccountGroupsTable;
